import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid } from '@mui/x-data-grid';
import { apiget, apiput } from '../../service/api';
import TableStyle from '../../components/TableStyle';
import { US_STATES } from './usStates';

/**
 * Admin-only Bankrate round-robin configuration panel.
 *
 * Three sections:
 *  - Master switch + shark-tank SLA minutes
 *  - Priority LOs (ordered — first in list wins, within the fairness band)
 *  - Officers table with inline edit for states + phone
 *
 * The per-LO "Accepting new leads" toggle is read-only here — only the
 * LO themselves can flip their own switch (see AcceptingLeadsSwitch.js).
 */
const RoundRobinSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [sharkTankMinutes, setSharkTankMinutes] = useState(5);
  const [priorityUserIds, setPriorityUserIds] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [editingOfficer, setEditingOfficer] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    const [settingsRes, officersRes] = await Promise.all([
      apiget('bankrate-lead/round-robin/settings'),
      apiget('bankrate-lead/round-robin/officers'),
    ]);
    if (settingsRes?.status === 200) {
      const s = settingsRes.data?.settings || {};
      setEnabled(s.enabled !== false);
      setSharkTankMinutes(s.sharkTankMinutes || 5);
      setPriorityUserIds((s.priorityUserIds || []).map(String));
    }
    if (officersRes?.status === 200) {
      setOfficers(officersRes.data?.officers || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const officerById = useMemo(() => {
    const map = new Map();
    officers.forEach((o) => map.set(String(o._id), o));
    return map;
  }, [officers]);

  const priorityOfficers = useMemo(
    () => priorityUserIds.map((id) => officerById.get(id)).filter(Boolean),
    [priorityUserIds, officerById]
  );

  const saveSettings = async (nextPatch = {}) => {
    setSaving(true);
    const body = {
      enabled,
      sharkTankMinutes: Number(sharkTankMinutes) || 5,
      priorityUserIds,
      ...nextPatch,
    };
    const res = await apiput('bankrate-lead/round-robin/settings', body);
    if (res?.status === 200 && res.data?.settings) {
      const s = res.data.settings;
      setEnabled(s.enabled !== false);
      setSharkTankMinutes(s.sharkTankMinutes || 5);
      setPriorityUserIds((s.priorityUserIds || []).map(String));
    }
    setSaving(false);
  };

  const handleMasterToggle = async (e) => {
    const next = e.target.checked;
    setEnabled(next);
    await saveSettings({ enabled: next });
  };

  const handlePriorityChange = async (_event, newValue) => {
    const ids = newValue.map((o) => String(o._id));
    setPriorityUserIds(ids);
    await saveSettings({ priorityUserIds: ids });
  };

  const handleSharkTankBlur = async () => {
    await saveSettings();
  };

  const openEditOfficer = (officer) => setEditingOfficer(officer);
  const closeEditOfficer = () => setEditingOfficer(null);

  const saveOfficerLicensing = async ({ states, phone }) => {
    if (!editingOfficer) return;
    const res = await apiput(`user/licensing/${editingOfficer._id}`, {
      states,
      phone,
    });
    if (res?.status === 200) {
      await loadAll();
      closeEditOfficer();
    }
  };

  const officerColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (p) =>
        [p.row.firstName, p.row.lastName].filter(Boolean).join(' ') || '—',
    },
    { field: 'emailAddress', headerName: 'Email', flex: 1.3 },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 0.9,
      renderCell: (p) => p.value || '—',
    },
    {
      field: 'states',
      headerName: 'Licensed States',
      flex: 1.6,
      renderCell: (p) => {
        const states = p.value || [];
        if (!states.length) {
          return <Typography variant="caption" color="text.secondary">None</Typography>;
        }
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {states.map((s) => (
              <Chip key={s} label={s} size="small" />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'roundRobinEnabled',
      headerName: 'Accepting',
      width: 120,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value ? 'Yes' : 'Paused'}
          color={p.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'recent30d', headerName: '30d', width: 70 },
    { field: 'lifetime', headerName: 'Lifetime', width: 90 },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => openEditOfficer(p.row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Round-robin enabled</Typography>
              <Typography variant="body2" color="text.secondary">
                Master switch. When off, incoming Bankrate leads are saved
                unassigned and appear in the manual-assignment bucket.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={handleMasterToggle}
                  disabled={saving}
                />
              }
              label={enabled ? 'On' : 'Off'}
            />
          </Stack>
          <Box sx={{ mt: 3 }}>
            <TextField
              label="Shark-tank SLA (minutes)"
              type="number"
              size="small"
              value={sharkTankMinutes}
              onChange={(e) => setSharkTankMinutes(e.target.value)}
              onBlur={handleSharkTankBlur}
              inputProps={{ min: 1, max: 1440 }}
              helperText="Minutes from assignment before a lead enters the shark tank."
              sx={{ maxWidth: 280 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6">Priority loan officers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These LOs get the next eligible lead first, as long as their
            recent-30 day count is within 2 of the minimum. Order is
            priority — first in the list wins.
          </Typography>
          <Autocomplete
            multiple
            options={officers}
            value={priorityOfficers}
            onChange={handlePriorityChange}
            getOptionLabel={(o) =>
              `${[o.firstName, o.lastName].filter(Boolean).join(' ')} (${
                o.emailAddress || ''
              })`
            }
            isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip
                  variant="filled"
                  color="primary"
                  label={`${i + 1}. ${[option.firstName, option.lastName]
                    .filter(Boolean)
                    .join(' ')}`}
                  {...getTagProps({ index: i })}
                  key={String(option._id)}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Add priority LO" />
            )}
            disabled={saving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6">Loan officers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Licensed states and phone numbers are edited by admins.
            Individual LOs control their own &ldquo;Accepting new leads&rdquo;
            switch from the Bankrate dashboard.
          </Typography>
          <TableStyle>
            <Box sx={{ width: '100%' }}>
              <DataGrid
                autoHeight
                rows={officers}
                columns={officerColumns}
                getRowId={(row) => row._id}
                disableSelectionOnClick
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>
          </TableStyle>
        </CardContent>
      </Card>

      <EditOfficerDialog
        officer={editingOfficer}
        onClose={closeEditOfficer}
        onSave={saveOfficerLicensing}
      />
    </Stack>
  );
};

function EditOfficerDialog({ officer, onClose, onSave }) {
  const [states, setStates] = useState([]);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (officer) {
      setStates(officer.states || []);
      setPhone(officer.phone || '');
    }
  }, [officer]);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ states, phone });
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(officer)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {officer
          ? `Edit licensing — ${[officer.firstName, officer.lastName]
              .filter(Boolean)
              .join(' ')}`
          : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Phone (for SMS notifications)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
            fullWidth
            placeholder="+15551234567"
          />
          <Autocomplete
            multiple
            options={US_STATES}
            value={states}
            onChange={(_, v) => setStates(v)}
            renderTags={(value, getTagProps) =>
              value.map((s, i) => (
                <Chip label={s} size="small" {...getTagProps({ index: i })} key={s} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Licensed states" placeholder="Add state" />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoundRobinSettings;
